import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { PoundSterling, TrendingUp, Clock, CheckCircle, Loader2, Users, Calendar, RefreshCw, Building2 } from 'lucide-react';
import type { Team, Commission, EarningsSummary } from '../../types';
import { getCommissionsForUser, getEarningsSummary } from '../../services/commissions';
import { formatCurrency } from '../../services/billing';

interface EarningsPageProps {
  team: Team;
  userId: string;
}

type StatusFilter = 'all' | 'pending' | 'approved' | 'paid';
type TimeRange = 'today' | '7d' | '30d' | 'all';

const EarningsPage: React.FC<EarningsPageProps> = ({ team, userId }) => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [summary, setSummary] = useState<EarningsSummary | null>(null);
  const [commissions, setCommissions] = useState<Commission[]>([]);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [timeRange, setTimeRange] = useState<TimeRange>('all');

  const fetchData = useCallback(async () => {
    const [summaryData, commissionsData] = await Promise.all([
      getEarningsSummary(team.id, userId),
      getCommissionsForUser(team.id, userId),
    ]);
    setSummary(summaryData);
    setCommissions(commissionsData);
  }, [team.id, userId]);

  useEffect(() => {
    setLoading(true);
    fetchData().finally(() => setLoading(false));
  }, [fetchData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
  };

  const filteredCommissions = useMemo(() => {
    let result = commissions;

    if (statusFilter !== 'all') {
      result = result.filter((c) => c.status === statusFilter);
    }

    if (timeRange !== 'all') {
      const now = new Date();
      let cutoff: Date;
      switch (timeRange) {
        case 'today':
          cutoff = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          break;
        case '7d':
          cutoff = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        case '30d':
          cutoff = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          break;
        default:
          cutoff = new Date(0);
      }
      result = result.filter((c) => new Date(c.created_at) >= cutoff);
    }

    return result;
  }, [commissions, statusFilter, timeRange]);

  const timeRangeStats = useMemo(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const week = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const month = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

    const todayComm = commissions.filter((c) => new Date(c.created_at) >= today);
    const weekComm = commissions.filter((c) => new Date(c.created_at) >= week);
    const monthComm = commissions.filter((c) => new Date(c.created_at) >= month);

    return {
      today: todayComm.reduce((sum, c) => sum + c.commission_amount_cents, 0),
      week: weekComm.reduce((sum, c) => sum + c.commission_amount_cents, 0),
      month: monthComm.reduce((sum, c) => sum + c.commission_amount_cents, 0),
    };
  }, [commissions]);

  const clientBreakdown = useMemo(() => {
    const byClient: Record<string, { name: string; company: string | null; revenue: number; commission: number; count: number }> = {};

    filteredCommissions.forEach((c) => {
      const contact = c.contact as { first_name?: string; last_name?: string; company?: string; email?: string } | null;
      const clientId = c.customer_contact_id || 'unknown';
      const name = contact
        ? [contact.first_name, contact.last_name].filter(Boolean).join(' ') || contact.email || 'Unknown'
        : 'Unknown';

      if (!byClient[clientId]) {
        byClient[clientId] = { name, company: contact?.company || null, revenue: 0, commission: 0, count: 0 };
      }
      byClient[clientId].revenue += c.revenue_amount_cents;
      byClient[clientId].commission += c.commission_amount_cents;
      byClient[clientId].count += 1;
    });

    return Object.entries(byClient)
      .sort(([, a], [, b]) => b.commission - a.commission)
      .slice(0, 10);
  }, [filteredCommissions]);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-amber-500/20 text-amber-400 rounded-full">
            <Clock size={10} />
            Pending
          </span>
        );
      case 'approved':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-blue-500/20 text-blue-400 rounded-full">
            <CheckCircle size={10} />
            Approved
          </span>
        );
      case 'paid':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-emerald-500/20 text-emerald-400 rounded-full">
            <CheckCircle size={10} />
            Paid
          </span>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">My Earnings</h1>
          <p className="text-slate-400 text-sm mt-1">Track your revenue and commission</p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center gap-2 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-slate-300 hover:text-white transition-colors disabled:opacity-50"
        >
          <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Time Range Quick Stats */}
      <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-slate-800/80 to-slate-900/50 border border-slate-700 rounded-xl">
        <Calendar size={20} className="text-orange-500" />
        <div className="flex items-center gap-6">
          <div>
            <div className="text-xs text-slate-400">Today</div>
            <div className="text-lg font-bold text-white">{formatCurrency(timeRangeStats.today, 'gbp')}</div>
          </div>
          <div className="w-px h-8 bg-slate-700" />
          <div>
            <div className="text-xs text-slate-400">Last 7 days</div>
            <div className="text-lg font-bold text-white">{formatCurrency(timeRangeStats.week, 'gbp')}</div>
          </div>
          <div className="w-px h-8 bg-slate-700" />
          <div>
            <div className="text-xs text-slate-400">Last 30 days</div>
            <div className="text-lg font-bold text-emerald-400">{formatCurrency(timeRangeStats.month, 'gbp')}</div>
          </div>
          <div className="w-px h-8 bg-slate-700" />
          <div>
            <div className="text-xs text-slate-400">All-time</div>
            <div className="text-lg font-bold text-white">{formatCurrency(summary?.total_commission_cents || 0, 'gbp')}</div>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="p-5 bg-slate-800/50 border border-slate-700 rounded-xl">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2.5 bg-blue-500/20 rounded-lg">
              <TrendingUp className="w-5 h-5 text-blue-400" />
            </div>
            <span className="text-sm font-medium text-slate-300">Total Revenue</span>
          </div>
          <div className="text-2xl font-bold text-white">
            {formatCurrency(summary?.total_revenue_cents || 0, 'gbp')}
          </div>
          <div className="text-xs text-slate-400 mt-1">
            From {summary?.customer_count || 0} customers
          </div>
        </div>

        <div className="p-5 bg-slate-800/50 border border-slate-700 rounded-xl">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2.5 bg-emerald-500/20 rounded-lg">
              <PoundSterling className="w-5 h-5 text-emerald-400" />
            </div>
            <span className="text-sm font-medium text-slate-300">Total Commission</span>
          </div>
          <div className="text-2xl font-bold text-emerald-400">
            {formatCurrency(summary?.total_commission_cents || 0, 'gbp')}
          </div>
          <div className="text-xs text-slate-400 mt-1">
            {summary?.commission_count || 0} transactions
          </div>
        </div>

        <div className="p-5 bg-slate-800/50 border border-slate-700 rounded-xl">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2.5 bg-amber-500/20 rounded-lg">
              <Clock className="w-5 h-5 text-amber-400" />
            </div>
            <span className="text-sm font-medium text-slate-300">Pending</span>
          </div>
          <div className="text-2xl font-bold text-amber-400">
            {formatCurrency(summary?.pending_commission_cents || 0, 'gbp')}
          </div>
          <div className="text-xs text-slate-400 mt-1">
            Awaiting approval
          </div>
        </div>

        <div className="p-5 bg-slate-800/50 border border-slate-700 rounded-xl">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2.5 bg-purple-500/20 rounded-lg">
              <CheckCircle className="w-5 h-5 text-purple-400" />
            </div>
            <span className="text-sm font-medium text-slate-300">Paid Out</span>
          </div>
          <div className="text-2xl font-bold text-white">
            {formatCurrency(summary?.paid_commission_cents || 0, 'gbp')}
          </div>
          <div className="text-xs text-slate-400 mt-1">
            Already received
          </div>
        </div>
      </div>

      {/* Client Breakdown */}
      {clientBreakdown.length > 0 && (
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-700">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <Building2 size={18} className="text-orange-500" />
              Commission by Client
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-900/50">
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Client</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-slate-400 uppercase tracking-wider">Transactions</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-slate-400 uppercase tracking-wider">Revenue</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-slate-400 uppercase tracking-wider">Commission</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700">
                {clientBreakdown.map(([id, client]) => (
                  <tr key={id} className="hover:bg-slate-800/30 transition-colors">
                    <td className="px-6 py-3">
                      <div className="text-sm font-medium text-white">{client.name}</div>
                      {client.company && <div className="text-xs text-slate-400">{client.company}</div>}
                    </td>
                    <td className="px-6 py-3 text-right text-sm text-slate-300">{client.count}</td>
                    <td className="px-6 py-3 text-right text-sm text-slate-300">{formatCurrency(client.revenue, 'gbp')}</td>
                    <td className="px-6 py-3 text-right text-sm font-medium text-emerald-400">{formatCurrency(client.commission, 'gbp')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Commission Table */}
      <div className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-700 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white">Commission History</h2>
          <div className="flex items-center gap-3">
            {/* Time Range Filter */}
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value as TimeRange)}
              className="px-3 py-1.5 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              <option value="all">All time</option>
              <option value="today">Today</option>
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
            </select>
            {/* Status Tabs */}
            <div className="flex border border-slate-700 rounded-lg overflow-hidden">
              {(['all', 'pending', 'approved', 'paid'] as const).map((status) => (
                <button
                  key={status}
                  onClick={() => setStatusFilter(status)}
                  className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                    statusFilter === status
                      ? 'bg-orange-600 text-white'
                      : 'bg-slate-800 text-slate-300 hover:text-white'
                  }`}
                >
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>

        {filteredCommissions.length === 0 ? (
          <div className="p-12 text-center">
            <Users className="w-12 h-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400">
              {commissions.length === 0 ? 'No commissions yet' : 'No commissions match your filters'}
            </p>
            <p className="text-sm text-slate-500 mt-1">
              {commissions.length === 0
                ? 'Link leads to ReHub accounts to start earning commission'
                : 'Try adjusting your filters'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-900/50">
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-slate-400 uppercase tracking-wider">
                    Revenue
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-slate-400 uppercase tracking-wider">
                    Rate
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-slate-400 uppercase tracking-wider">
                    Commission
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-slate-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                    Payout
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700">
                {filteredCommissions.map((commission) => {
                  const contact = commission.contact as { first_name?: string; last_name?: string; company?: string; email?: string } | null;
                  const customerName = contact
                    ? [contact.first_name, contact.last_name].filter(Boolean).join(' ') || contact.company || contact.email || 'Unknown'
                    : 'Unknown Customer';

                  return (
                    <tr key={commission.id} className="hover:bg-slate-800/30 transition-colors">
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-white">{customerName}</div>
                        {contact?.company && customerName !== contact.company && (
                          <div className="text-xs text-slate-400">{contact.company}</div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-slate-300">
                          {new Date(commission.created_at).toLocaleDateString('en-GB', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                          })}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="text-sm text-white">
                          {formatCurrency(commission.revenue_amount_cents, commission.currency)}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="text-sm text-slate-300">
                          {(commission.commission_rate * 100).toFixed(0)}%
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="text-sm font-medium text-emerald-400">
                          {formatCurrency(commission.commission_amount_cents, commission.currency)}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        {getStatusBadge(commission.status)}
                      </td>
                      <td className="px-6 py-4">
                        {commission.paid_at ? (
                          <div>
                            <div className="text-xs text-slate-300">
                              {new Date(commission.paid_at).toLocaleDateString('en-GB', {
                                day: 'numeric',
                                month: 'short',
                              })}
                            </div>
                            {commission.payout_ref && (
                              <div className="text-xs text-slate-500 truncate max-w-[100px]" title={commission.payout_ref}>
                                {commission.payout_ref}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-slate-500">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="px-6 py-3 border-t border-slate-700 bg-slate-900/30 flex items-center justify-between">
              <span className="text-sm text-slate-400">
                Showing {filteredCommissions.length} of {commissions.length} commissions
              </span>
              <span className="text-sm font-medium text-white">
                Total: {formatCurrency(filteredCommissions.reduce((sum, c) => sum + c.commission_amount_cents, 0), 'gbp')}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EarningsPage;
